// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// 감사 알림 규칙 관리 모달. 단순 CRUD + enable 토글.
// action_kind 또는 severity 중 하나 이상 지정해야 함(backend validation).
// 이 두 옵션 목록은 audit.tsx의 ACTION_LABEL과 일치해야 — 호출자가 props로 전달.

const EMPTY_FORM = { name: "", action_kind: "", severity: "", recipient_email: "", enabled: true, notes: "" };

export function AuditAlertRulesModal({ actionLabels, onClose }) {
  useEscapeKey(onClose);
  const [rules, setRules] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | "new" | id
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/v1/audit_alert_rules", { credentials: "same-origin" });
      if (!r.ok) throw new Error(`오류 (${r.status})`);
      setRules(await r.json());
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing("new"); setErr(null); };
  const openEdit = (rule) => {
    setForm({
      name: rule.name, action_kind: rule.action_kind || "", severity: rule.severity || "",
      recipient_email: rule.recipient_email, enabled: !!rule.enabled, notes: rule.notes || "",
    });
    setEditing(rule.id);
    setErr(null);
  };
  const closeForm = () => { setEditing(null); setErr(null); };

  const save = async () => {
    setErr(null);
    const isNew = editing === "new";
    const url    = isNew ? "/api/v1/audit_alert_rules" : `/api/v1/audit_alert_rules/${editing}`;
    const method = isNew ? "POST" : "PATCH";
    // 빈 문자열은 backend로 null로 전송하려면 명시적 처리.
    const payload = { ...form,
      action_kind: form.action_kind || null,
      severity:    form.severity    || null,
      notes:       form.notes       || null,
    };
    try {
      const r = await fetch(url, {
        method, credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(apiErrorMessage(j, r.status));
        return;
      }
      closeForm();
      load();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const remove = async (rule) => {
    if (!confirm(`"${rule.name}" 규칙을 삭제합니다.\n진행하시겠습니까?`)) return;
    const r = await fetch(`/api/v1/audit_alert_rules/${rule.id}`, {
      method: "DELETE", credentials: "same-origin",
    });
    if (!r.ok) { const j = await r.json().catch(() => ({})); alert(apiErrorMessage(j, r.status)); return; }
    load();
  };

  const toggle = async (rule) => {
    const r = await fetch(`/api/v1/audit_alert_rules/${rule.id}`, {
      method: "PATCH", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: !rule.enabled }),
    });
    if (!r.ok) { const j = await r.json().catch(() => ({})); alert(apiErrorMessage(j, r.status)); return; }
    load();
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:60}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:720, maxHeight:'85vh', padding:0, display:'flex', flexDirection:'column'}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">감사 알림 규칙</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>특정 감사 이벤트 발생 시 이메일 알림</div>
          </div>
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            {!editing && (
              <button type="button" className="btn btn-primary btn-sm" onClick={openNew}>{Ic.plus} 규칙 추가</button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
          </div>
        </div>

        {err && !editing && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

        <div style={{padding:22, overflow:'auto', flex:1}}>
          {editing ? (
            <RuleForm form={form} setForm={setForm} actionLabels={actionLabels} err={err}
              onCancel={closeForm} onSave={save} isNew={editing === "new"}/>
          ) : loading ? (
            <div className="muted" style={{fontSize:12, padding:18, textAlign:'center'}}>불러오는 중…</div>
          ) : rules.length === 0 ? (
            <div className="muted" style={{fontSize:12.5, padding:30, textAlign:'center'}}>
              아직 등록된 규칙이 없습니다. "규칙 추가"로 시작하세요.
            </div>
          ) : (
            <table className="tbl">
              <thead><tr>
                <th>이름</th><th>조건</th><th>수신자</th><th style={{width:80}}>상태</th>
                <th style={{textAlign:'right', width:120}}></th>
              </tr></thead>
              <tbody>
                {rules.map(r => {
                  const ak = r.action_kind && (actionLabels[r.action_kind]?.label || r.action_kind);
                  return (
                    <tr key={r.id}>
                      <td><b>{r.name}</b>{r.notes && <div className="muted" style={{fontSize:10.5}}>{r.notes}</div>}</td>
                      <td style={{fontSize:11.5}}>
                        {ak && <div>행위: <code style={{background:'var(--bg-sub)', padding:'1px 5px', borderRadius:3}}>{ak}</code></div>}
                        {r.severity && <div>심각도: <b>{r.severity}</b></div>}
                        {!ak && !r.severity && <span className="muted">—</span>}
                      </td>
                      <td className="mono muted" style={{fontSize:11.5}}>{r.recipient_email}</td>
                      <td>
                        <button type="button" className="btn btn-ghost btn-sm"
                          onClick={()=>toggle(r)}
                          style={{color: r.enabled ? 'var(--green-600)' : 'var(--ink-3)'}}>
                          {r.enabled ? '✓ 활성' : '비활성'}
                        </button>
                      </td>
                      <td style={{textAlign:'right'}}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(r)}>편집</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>remove(r)} style={{color:'var(--rose-500)'}}>삭제</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function RuleForm({ form, setForm, actionLabels, err, onCancel, onSave, isNew }) {
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
      {err && <div style={{gridColumn:'1 / span 2', padding:'8px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:12}}>{err}</div>}
      <div style={{gridColumn:'1 / span 2'}}>
        <div className="label">규칙 이름 *</div>
        <input className="input" value={form.name} onChange={e=>set('name')(e.target.value)} placeholder="예: 로그인 실패 즉시 알림" required/>
      </div>
      <div>
        <div className="label">매칭할 행위 (선택)</div>
        <select className="select" value={form.action_kind} onChange={e=>set('action_kind')(e.target.value)}>
          <option value="">— 모든 행위 —</option>
          {Object.entries(actionLabels).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <div>
        <div className="label">매칭할 심각도 (선택)</div>
        <select className="select" value={form.severity} onChange={e=>set('severity')(e.target.value)}>
          <option value="">— 모든 심각도 —</option>
          <option value="info">정보</option>
          <option value="warn">주의</option>
          <option value="crit">위험</option>
        </select>
      </div>
      <div style={{gridColumn:'1 / span 2'}}>
        <div className="label">수신자 이메일 *</div>
        <input className="input" type="email" value={form.recipient_email} onChange={e=>set('recipient_email')(e.target.value)} placeholder="ops@care.local" required/>
      </div>
      <div style={{gridColumn:'1 / span 2'}}>
        <div className="label">메모</div>
        <input className="input" value={form.notes} onChange={e=>set('notes')(e.target.value)} placeholder="예: 5월부터 한시 운영"/>
      </div>
      <div style={{gridColumn:'1 / span 2'}}>
        <label style={{display:'flex', alignItems:'center', gap:8, fontSize:12.5}}>
          <input type="checkbox" checked={form.enabled} onChange={e=>set('enabled')(e.target.checked)}/>
          활성 — 체크 해제 시 매칭되어도 메일 발송 안 함
        </label>
      </div>
      <div className="muted" style={{gridColumn:'1 / span 2', fontSize:11, padding:'8px 10px', background:'var(--amber-50)', color:'#8a6326', borderRadius:8}}>
        ⚠ 행위와 심각도를 모두 비우면 모든 감사가 메일이 됩니다(폭주 위험). 최소 하나는 지정하세요.
      </div>
      <div style={{gridColumn:'1 / span 2', display:'flex', justifyContent:'flex-end', gap:8, marginTop:6}}>
        <button type="button" className="btn btn-ghost btn-md" onClick={onCancel}>취소</button>
        <button type="button" className="btn btn-primary btn-md" onClick={onSave}
          disabled={!form.name || !form.recipient_email}>
          {isNew ? '추가' : '저장'}
        </button>
      </div>
    </div>
  );
}
